import React, { useCallback, useEffect, useMemo, useState } from "react";
import SafeIcon from "../../common/SafeIcon";
import { FiActivity, FiArrowDownLeft, FiArrowUpRight, FiLayers } from "react-icons/fi";
import { useClient } from "../../context/ClientContext";
import { clientTransactionService } from "../../services/client/clientTransactionService";
import ClientTransactionFilters from "../../components/clients/transactions/ClientTransactionFilters";
import ClientTransactionTable from "../../components/clients/transactions/ClientTransactionTable";
import ClientTransactionDetailsModal from "../../components/clients/modals/ClientTransactionDetailsModal";

const PAGE_SIZE = 20;

function formatCurrency(value, currency) {
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency,
    }).format(value);
  } catch {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: "USD",
    }).format(value);
  }
}

function SummaryCard({ title, value, currency, icon, tone, note }) {
  return (
    <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-sm font-medium text-slate-500">{title}</p>
          <p className="mt-2 text-2xl font-black tracking-tight text-slate-900">
            {title === "Total Transactions"
              ? value.toLocaleString()
              : formatCurrency(value, currency)}
          </p>
          <p className="mt-1 text-xs text-slate-400">{note}</p>
        </div>
        <span className={`flex h-10 w-10 items-center justify-center rounded-xl ${tone}`}>
          <SafeIcon icon={icon} className="text-lg" />
        </span>
      </div>
    </section>
  );
}

export default function ClientTransactions() {
  const { activeWorkspace } = useClient();
  const organizationId = activeWorkspace?.id;
  const organizationName = activeWorkspace?.name || "Your business";
  const currency = (activeWorkspace?.currency || "USD").toUpperCase();

  const [transactions, setTransactions] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [loadedOrganizationId, setLoadedOrganizationId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [page, setPage] = useState(1);
  const [sortDirection, setSortDirection] = useState("desc");
  const [filters, setFilters] = useState({
    search: "",
    startDate: "",
    endDate: "",
    accountId: "all",
  });

  useEffect(() => {
    let current = true;

    if (!organizationId) {
      setTransactions([]);
      setAccounts([]);
      setLoadedOrganizationId(null);
      setLoading(false);
      setError("Select a business to view transactions.");
      return () => {
        current = false;
      };
    }

    setLoading(true);
    setError("");
    setTransactions([]);
    setAccounts([]);
    setLoadedOrganizationId(null);
    setSelectedTransaction(null);

    clientTransactionService
      .getClientTransactions(organizationId)
      .then((result) => {
        if (!current) return;
        setTransactions(result.transactions);
        setAccounts(result.accounts);
        setLoadedOrganizationId(organizationId);
      })
      .catch((loadError) => {
        if (!current) return;
        setError(
          loadError?.message ||
            "Unable to load transactions for this business.",
        );
      })
      .finally(() => {
        if (current) setLoading(false);
      });

    return () => {
      current = false;
    };
  }, [organizationId]);

  useEffect(() => {
    setPage(1);
  }, [filters, organizationId, sortDirection]);

  const filteredTransactions = useMemo(() => {
    if (loadedOrganizationId !== organizationId) return [];

    const query = filters.search.trim().toLocaleLowerCase();

    return transactions
      .filter((transaction) => {
        if (filters.startDate && transaction.date < filters.startDate) {
          return false;
        }
        if (filters.endDate && transaction.date > filters.endDate) {
          return false;
        }
        if (
          filters.accountId !== "all" &&
          transaction.account_id !== filters.accountId
        ) {
          return false;
        }
        if (!query) return true;

        const searchable = [
          transaction.description,
          transaction.batch_id,
          transaction.source,
          transaction.account?.name,
          transaction.account?.code,
        ]
          .filter(Boolean)
          .join(" ")
          .toLocaleLowerCase();

        return searchable.includes(query);
      })
      .sort((first, second) => {
        const comparison = String(first.date || "").localeCompare(
          String(second.date || ""),
        );
        if (comparison !== 0) {
          return sortDirection === "asc" ? comparison : -comparison;
        }
        return String(second.created_at || "").localeCompare(
          String(first.created_at || ""),
        );
      });
  }, [filters, loadedOrganizationId, organizationId, sortDirection, transactions]);

  const totals = useMemo(
    () =>
      filteredTransactions.reduce(
        (summary, transaction) => ({
          debit: summary.debit + (Number(transaction.debit) || 0),
          credit: summary.credit + (Number(transaction.credit) || 0),
        }),
        { debit: 0, credit: 0 },
      ),
    [filteredTransactions],
  );

  const handleFilterChange = (name, value) => {
    setFilters((current) => ({ ...current, [name]: value }));
  };

  const handleReset = () => {
    setFilters({
      search: "",
      startDate: "",
      endDate: "",
      accountId: "all",
    });
  };

  const closeDetails = useCallback(() => {
    setSelectedTransaction(null);
  }, []);

  const showingData = loadedOrganizationId === organizationId;

  return (
    <div className="mx-auto max-w-7xl space-y-6">
      <header className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <p className="text-xs font-bold uppercase tracking-widest text-violet-700">
            Financial management
          </p>
          <h1 className="mt-2 text-3xl font-black tracking-tight text-slate-900">
            Transactions
          </h1>
          <p className="mt-2 text-sm text-slate-600">
            View and track your business&apos;s financial transactions.
          </p>
        </div>
        <div className="rounded-xl border border-slate-200 bg-white px-4 py-3 shadow-sm">
          <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
            Selected business
          </p>
          <p className="mt-1 max-w-xs truncate text-sm font-bold text-slate-800">
            {organizationName}
          </p>
        </div>
      </header>

      {error && (
        <div
          role="alert"
          className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-800"
        >
          <p className="font-semibold">Transactions could not be loaded</p>
          <p className="mt-1">{error}</p>
        </div>
      )}

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <SummaryCard
          title="Total Transactions"
          value={showingData ? filteredTransactions.length : 0}
          currency={currency}
          icon={FiLayers}
          tone="bg-violet-50 text-violet-700"
          note="Matching the current filters"
        />
        <SummaryCard
          title="Total Debits"
          value={showingData ? totals.debit : 0}
          currency={currency}
          icon={FiArrowDownLeft}
          tone="bg-emerald-50 text-emerald-700"
          note="Ledger debit entries"
        />
        <SummaryCard
          title="Total Credits"
          value={showingData ? totals.credit : 0}
          currency={currency}
          icon={FiArrowUpRight}
          tone="bg-blue-50 text-blue-700"
          note="Ledger credit entries"
        />
        <SummaryCard
          title="Net Activity"
          value={showingData ? totals.debit - totals.credit : 0}
          currency={currency}
          icon={FiActivity}
          tone="bg-amber-50 text-amber-700"
          note="Debits less credits"
        />
      </div>

      <ClientTransactionFilters
        {...filters}
        accounts={accounts}
        onChange={handleFilterChange}
        onReset={handleReset}
      />

      <ClientTransactionTable
        transactions={filteredTransactions}
        loading={loading}
        currency={currency}
        page={page}
        pageSize={PAGE_SIZE}
        sortDirection={sortDirection}
        onSortDate={() =>
          setSortDirection((direction) =>
            direction === "desc" ? "asc" : "desc",
          )
        }
        onPageChange={setPage}
        onView={setSelectedTransaction}
      />

      {!loading && showingData && transactions.length === 0 && !error && (
        <p className="text-center text-xs text-slate-500">
          No ledger transactions have been recorded for this business.
        </p>
      )}

      <ClientTransactionDetailsModal
        transaction={selectedTransaction}
        currency={currency}
        onClose={closeDetails}
      />
    </div>
  );
}